import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Chip, IconButton, Paper, Slider, Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import OperationsMap from '../operations/OperationsMap';
import { formatDateTime } from '../common/util/datetime';
import { hoursAndMinutes } from '../operations/stageWords';
import {
  clockOf, cursorAt, spanOf, journeyEvents, speedOf, GAP_MS,
} from './playbackClock';

// The trip played back, from where it started to where it has got to.
//
// A trail drawn all at once answers where a lorry went; it does not answer when, which is the
// question somebody asks when a customer says the load was late or a driver says the plant held him
// up. So the same recorded fixes are walked through in order: the line grows, the truck moves along
// it, and the clock beside it says what time it was. For a trip still running the end of the line is
// the last fix received, so pressing play and leaving it is how the plant watches a lorry arrive.
//
// Nothing here fetches anything. The fixes are the ones the page already holds, so playback costs
// one timer and no requests, however often somebody scrubs back and forth.
const SPEEDS = [
  [60, '1 min/s'],
  [300, '5 min/s'],
  [1800, '30 min/s'],
];
const TripPlayback = ({
  trail = [], label, sites = [], live = false, latest = null, height = 360, plan = [],
  detections = [], limitKph = 0,
}) => {
  const frames = useMemo(() => trail.filter((p) => Number.isFinite(Date.parse(p.fixTime))), [trail]);
  const times = useMemo(() => clockOf(frames), [frames]);
  const span = spanOf(times);
  // Where playback stands, in milliseconds of trip time rather than in samples. Opens at the end,
  // which is where a running trip is now and where a finished one left off.
  const [elapsedMs, setElapsedMs] = useState(span);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(300);
  const timer = useRef(null);
  const last = frames.length ? frames.length - 1 : 0;

  // A trip still running gains fixes while it is being watched. Somebody parked at the end of the
  // trail is following the lorry and should keep following it; somebody who has scrubbed back is
  // reading a particular minute and must not be dragged forward out of it.
  const grown = useRef(span);
  useEffect(() => {
    if (elapsedMs >= grown.current) setElapsedMs(span);
    grown.current = span;
  }, [span]);

  useEffect(() => {
    if (!playing || frames.length < 2) return undefined;
    timer.current = window.setInterval(() => {
      setElapsedMs((current) => {
        // One second of watching covers `rate` seconds of the trip - of the trip's clock, not of its
        // samples. The old rule advanced at least one fix per tick, so an hour between fixes was
        // crossed in a second however slowly playback was set.
        const next = current + rate * 1000;
        if (next >= span) { setPlaying(false); return span; }
        return next;
      });
    }, 1000);
    return () => window.clearInterval(timer.current);
  }, [playing, rate, span, frames.length]);

  // What happened, in the order it happened. Rebuilt only when the trail or the recorded stages
  // change, because it walks every fix.
  const events = useMemo(
    () => journeyEvents({
      frames, times, detections, limitKph,
    }),
    [frames, times, detections, limitKph],
  );

  // Hooks are all above this line. Both screens render once before their positions arrive, so a
  // hook below the branch is skipped on that render and added on the next one, which React treats
  // as a different component and refuses to reconcile.
  if (frames.length < 2) {
    return (
      <Box sx={{ mb: 3 }}>
        {/* The one observation there is, shown. Playback needs two fixes; knowing where the lorry
            was does not, and withholding it left the only useful thing on the page off the map. */}
        <OperationsMap
          height={height}
          points={frames.length ? [...sites, { ...frames[0], id: 'playback', label }] : sites}
          plan={plan}
        />
        <Typography variant="caption" color="text.secondary">
          {frames.length
            ? `One recorded fix, at ${formatDateTime(frames[0].fixTime)}. Playback needs a second.`
            : 'No recorded positions for this trip yet.'}
        </Typography>
      </Box>
    );
  }

  const { index, gapMs } = cursorAt(times, elapsedMs);
  const atEnd = elapsedMs >= span;
  // At the end of a running trip the live feed knows where the lorry is now, which is later than its
  // last recorded fix. Anywhere else the recorded fix is the truth.
  const here = atEnd && latest ? { ...frames[last], ...latest } : frames[index];
  // Long enough that it is a hole in the record rather than the ordinary space between fixes.
  const silentFor = gapMs >= GAP_MS ? gapMs : 0;
  return (
    <Box sx={{ mb: 3 }}>
      <OperationsMap
        height={height}
        points={[...sites, { ...here, id: 'playback', label }]}
        trail={frames.slice(0, index + 1)}
        frame={frames}
        follow={playing ? here : null}
        plan={plan}
      />
      <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <IconButton
            aria-label={playing ? 'Pause playback' : 'Play the trip back'}
            onClick={() => { if (atEnd) setElapsedMs(0); setPlaying((on) => !on); }}
          >
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <IconButton aria-label="Back to the start of the trip" onClick={() => { setPlaying(false); setElapsedMs(0); }}><ReplayIcon /></IconButton>
          <Box sx={{ flex: '1 1 240px', px: 1 }}>
            {/* The journey's own clock. As a sample index, a forty-minute stop and a thirty-second
                gap took the same width, and the middle of the slider was wherever the fixes
                happened to be dense rather than half way through. */}
            <Slider
              size="small"
              min={0}
              max={Math.max(span, 1)}
              value={Math.min(elapsedMs, span)}
              onChange={(event, value) => { setPlaying(false); setElapsedMs(value); }}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => hoursAndMinutes(Math.round(value / 60000))}
              aria-label="Time through the journey"
            />
          </Box>
          <Box sx={{ minWidth: 190 }}>
            <Typography variant="body2" fontWeight={600}>{formatDateTime(here.fixTime)}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {/* Since the first recorded fix, which is named rather than called "into the trip":
                  a lorry can be admitted long before its tracker reports, and reading one as the
                  other is how an hour goes missing in an argument. */}
              {`${hoursAndMinutes(Math.round(elapsedMs / 60000))} since the first fix · ${index + 1} of ${frames.length}${speedOf(here) != null ? ` · ${Math.round(speedOf(here))} km/h` : ''}`}
            </Typography>
            {silentFor > 0 && (
              <Typography variant="caption" color="warning.main" display="block">
                {`No fix for ${hoursAndMinutes(Math.round(silentFor / 60000))} — position is the last one recorded`}
              </Typography>
            )}
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={rate}
            onChange={(event, value) => value && setRate(value)}
            aria-label="Playback speed"
          >
            {SPEEDS.map(([value, name]) => <ToggleButton key={value} value={value}>{name}</ToggleButton>)}
          </ToggleButtonGroup>
        </Stack>
        {events.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              What happened on this trip — select one to go to it
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {events.map((event) => (
                <Chip
                  key={event.key}
                  size="small"
                  variant={Math.abs(elapsedMs - event.at) < 30000 ? 'filled' : 'outlined'}
                  color={event.kind === 'SPEED' ? 'error' : event.kind === 'GAP' ? 'warning' : event.kind === 'ARRIVED' ? 'success' : 'default'}
                  onClick={() => { setPlaying(false); setElapsedMs(Math.max(0, Math.min(event.at, span))); }}
                  label={
                    event.kind === 'ARRIVED'
                      ? `${event.label} · ${formatDateTime(event.when)}`
                      : event.kind === 'SPEED'
                        ? `${event.label} · ${event.peakKph} km/h`
                        : `${event.label} · ${hoursAndMinutes(Math.round(event.ms / 60000))}`
                  }
                />
              ))}
            </Stack>
          </Box>
        )}
        {live && atEnd && (
          <Typography variant="caption" color="text.secondary">At the latest fix; this trip is still running, so the trail grows as the lorry reports.</Typography>
        )}
      </Paper>
    </Box>
  );
};

export default TripPlayback;
